<template>
  <DynamicModelCustomFormShell
    v-if="useAutonomousShell"
    :mode="isAddMode ? 'add' : 'edit'"
    :embedded="embedded"
    :embed-model-name="embedModelName"
    :embed-item-id="embedItemId"
  />
  <div
    v-else
    class="container-fluid"
    :class="{ 'dynamic-model-edit--embedded': embedded }"
  >
    <!-- Loading overlay component -->
    <LoadingOverlay
      :show="isSubmitting"
      message="Saving changes..."
      loading-text="Saving..."
    />
    <ContentSkeleton
      v-if="!selectedModelData || !selectedModelData.modelName"
      variant="accordion"
      :with-title="!embedded"
      class="mt-2"
    />

    <div >
      <!-- Todo en un renglón: el título ocupaba tres veces el alto que necesita. -->
      <div
        class="d-flex align-items-center justify-content-between gap-2 mb-2 mt-2"
        v-if="selectedModelData && !embedded"
      >
        <h2 class="dynamic-model-edit-title mb-0">
          <span>
            {{ isAddMode ? $t('New') : $t('Edit') }}
            {{
              selectedModelData.label
                ? $t(selectedModelData.label)
                : $t(selectedModelData.modelName)
            }}
          </span>
          <span
            v-if="!isAddMode && item && item.idModel"
            class="dynamic-model-edit-title-ref"
            >{{ item.name }}</span
          >
        </h2>
        <div class="d-flex align-items-center gap-2">
          <FormLayoutMenu
            v-if="selectedModel && useFormV2 && !customFormComponent"
            ref="formLayoutMenu"
            :model-name="selectedModel"
            @change="onFormLayoutChange"
            @edit-widths="editingFormWidths = true"
          />
          <button
            class="btn-primary btn btn-sm"
            @click="goBack"
          >
            <i class="fas fa-arrow-left"></i> {{ $t('Back') || 'Back' }}
          </button>
        </div>
      </div>
      <ShippingIncoherenceBanner
        v-if="!isAddMode && !embedded && routeItemId"
        :model-name="selectedModel"
        :item-id="String(routeItemId)"
        :reload-key="savedCount"
      />
      <div v-if="selectedModel && (item || isAddMode)">
        <div
          class="card container-form"
          :class="[{ 'border-0 shadow-none': embedded }, formLayoutClass(!embedded)]"
          :style="formLayoutStyle(!embedded)"
        >
          <div class="card-body" :class="{ 'p-0 p-md-1': embedded }">
            <FormColumnRuler
              v-if="editingFormWidths && formColumns && !embedded"
              :columns="formColumns"
              :value="formColumnWidth"
              @input="formColumnWidth = $event"
              @done="onFormWidthsDone"
              @cancel="onFormWidthsCancel"
            />
            <div v-if="!modelFields.length">No hay campos definidos</div>
            <div v-if="!isAddMode && !item">No hay datos del item</div>
            <AutoSaveForm
              v-if="modelFields.length && (item || isAddMode)"
              ref="autoSaveForm"
              :formData="form"
              :modelName="selectedModel"
              :processFormData="processFormData"
              :modelFields="modelFields"
              :parentID="null"
              :parentModel="null"
              :disabled="isAutoSaveDisabledByModel"
              :manual-save-in-progress="isSubmitting"
              @unsaved-changes="onUnsavedChanges"
              @auto-save-success="onAutoSaveSuccess"
              @auto-save-error="onAutoSaveError"
            >
              <component
                v-if="customFormComponent"
                :is="customFormComponent"
                ref="formV2"
                :key="'custom-' + formId"
                :fields="modelFields"
                :value="form"
                :lists="lists"
                :visualGroups="sortedVisualGroups()"
                :selectedModel="selectedModel"
                :modelDefinitions="modelDefinitions"
                :models="models"
                :save-in-progress="isSubmitting"
                :embedded="embedded"
                @submit="onSubmit"
                @cancel="onCancel"
                @input="onInput"
                @button-click="onButtonClick"
              />
              <FormV2
                v-else-if="useFormV2"
                ref="formV2"
                :key="'v2-' + formId"
                :fields="modelFields"
                :value="form"
                :lists="lists"
                :visualGroups="sortedVisualGroups()"
                :selectedModel="selectedModel"
                :modelDefinitions="modelDefinitions"
                :models="models"
                :save-in-progress="isSubmitting"
                @submit="onSubmit"
                @cancel="onCancel"
                @input="onInput"
                @button-click="onButtonClick"
              />
              <Form
                v-else
                ref="legacyForm"
                :key="formId"
                :fields="modelFields"
                :value="form"
                :lists="lists"
                :visualGroups="sortedVisualGroups()"
                :selectedModel="selectedModel"
                :modelDefinitions="modelDefinitions"
                :models="models"
                :save-in-progress="isSubmitting"
                @submit="onSubmit"
                @cancel="onCancel"
                @input="onInput"
                @button-click="onButtonClick"
              />
            </AutoSaveForm>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Form from "@/components/gotrading/common/Form.vue";
import {
  findReferenceFieldSuggestion,
  fetchReferenceFieldSuggestions,
  isEmptyFormValue,
  referenceIdOf,
} from "@/utils/referenceFieldSuggestions";
import FormV2 from "@/components/gotrading/common/FormV2.vue";
import FormLayoutMenu from "@/components/gotrading/common/FormLayoutMenu.vue";
import FormColumnRuler from "@/components/gotrading/common/FormColumnRuler.vue";
import formLayoutMixin from "@/mixins/formLayoutMixin";
import AutoSaveForm from "@/components/gotrading/common/AutoSaveForm.vue";
import { newClientCreateKey } from "@/utils/clientCreateKey";
import Breadcrumb from "@/components/gotrading/common/Breadcrumb.vue";
import { setCurrentItemLabel, buildItemDisplayName } from "@/utils/currentItemLabel";
import LoadingOverlay from "@/components/common/LoadingOverlay.vue";
import ContentSkeleton from "@/components/common/ContentSkeleton.vue";
import { wencesApi } from "@/api/wencesApi";
import { promptFindTeuTrackingOnSave } from "@/utils/findTeuPromptTrackingOnSave";
import Swal from "sweetalert2";
import {
  confirmDuntowerExpenseSaveWithoutFile,
  getApiErrorData,
  isHookValidationError,
  promptHookValidationForceSave,
  showForceSaveRetryError,
} from "@/utils/dynamicModelValidationSwal";
import { resolveDynamicFormComponent, isAutonomousDynamicForm } from "@/utils/dynamicFormRegistry";
import ShippingIncoherenceBanner from "@/components/gotrading/common/ShippingIncoherenceBanner.vue";
import DynamicModelCustomFormShell from "@/components/gotrading/dynamic/DynamicModelCustomFormShell.vue";
import { copyTranslatableSiblingsInto } from "@/utils/fieldValueI18n";
import offerNotaComercialOnOperationCreateMixin from "@/modules/nota_comercial_module/mixins/offerNotaComercialOnOperationCreateMixin";

export default {
  name: "DynamicModelEdit",
  mixins: [offerNotaComercialOnOperationCreateMixin, formLayoutMixin],
  components: {
    ShippingIncoherenceBanner,
    Form,
    FormV2,
    AutoSaveForm,
    Breadcrumb,
    LoadingOverlay,
    ContentSkeleton,
    DynamicModelCustomFormShell,
    FormLayoutMenu,
    FormColumnRuler,
  },
  props: {
    /** Cuando true, modelName e itemId vienen de props (vista embebida, p.ej. FOB Operación). */
    embedded: {
      type: Boolean,
      default: false,
    },
    embedModelName: {
      type: String,
      default: "",
    },
    embedItemId: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      /** Sube con cada guardado: el cartel de incoherencias vuelve a consultar. */
      savedCount: 0,
      // Cambiar a true para usar FormV2; false para Form.vue
      useFormV2: true,
      formId: 1000,
      models: [],
      selectedModel: "",
      selectedModelData: null,
      modelFields: [],
      item: null,
      form: {},
      lists: [],
      referenceItems: {},
      modelDefinitions: {},
      lockInterval: null,
      isSubmitting: false,
      // Clave de alta de este formulario: el backend crea un solo documento por clave aunque
      // el autoguardado y «Guardar» pidan el alta a la vez (ver utils/clientCreateKey).
      clientCreateKey: newClientCreateKey(),
      // Protección contra ejecuciones redundantes de onInput
      isProcessingInput: false,
      lastFormDataSnapshot: null,
      lastOnInputTime: null,
      onInputCallCount: 0,
      onInputCallHistory: [],
      // Cola para procesar solo el evento más reciente
      pendingInputEvent: null,
      inputDebounceTimer: null,
      customFormComponent: null,
      useAutonomousShell: false,
      formMeta: {},
      formSchemaDefaults: {},
      isLoadingFormSchema: false,
      /** El autocompletado por historial recién se activa con el ítem ya cargado. */
      referenceSuggestionsArmed: false,
    };
  },
  computed: {
    routeModelName() {
      if (this.embedded && this.embedModelName) {
        return this.embedModelName;
      }
      return this.$route.params.modelName || "";
    },
    routeItemId() {
      if (this.embedded && this.embedItemId) {
        return this.embedItemId;
      }
      return this.$route.params.itemId || "";
    },
    /** Presente cuando se llegó a editar desde una vista concreta (p.ej. PDF comprador/vendedor). */
    routeViewId() {
      if (this.embedded) return "";
      return this.$route.params.viewId || "";
    },
    isAddMode() {
      const itemId = this.routeItemId;
      return itemId == null || itemId === "" || itemId === "new";
    },
    isAutoSaveDisabledByModel() {
      return this.selectedModelData && this.selectedModelData.autoSave === false;
    },
    /** Regla de autocompletado por historial para este modelo (si aplica). */
    referenceSuggestionRule() {
      return findReferenceFieldSuggestion(this.selectedModel, this.modelFields);
    },
    referenceSuggestionSourceId() {
      const rule = this.referenceSuggestionRule;
      if (!rule) return "";
      return referenceIdOf(this.form && this.form[rule.sourceField]);
    },
  },
  watch: {
    referenceSuggestionSourceId(newVal, oldVal) {
      if (!this.referenceSuggestionsArmed) return;
      if (!newVal || newVal === oldVal) return;
      this.applyReferenceFieldSuggestions();
    },
    routeModelName: {
      immediate: true,
      handler(newVal) {
        if (newVal && this.models.length) {
          const found = this.models.find(
            (m) => m.modelName.toLowerCase() === newVal.toLowerCase()
          );
          if (found) {
            this.selectedModel = found.modelName;
            this.fetchFields();
          }
        }
      },
    },
    routeItemId: {
      immediate: true,
      handler(newVal) {
        if (newVal && this.selectedModel) {
          this.fetchItem(newVal);
        }
      },
    },
    models: {
      immediate: true,
      handler(newVal) {
        const modelName = this.routeModelName;
        if (modelName && newVal.length) {
          const found = newVal.find(
            (m) => m.modelName.toLowerCase() === modelName.toLowerCase()
          );
          if (found) {
            this.selectedModel = found.modelName;
            this.fetchFields();
          }
        }
      },
    },
  },
  methods: {
    sortedVisualGroups() {
      if (!this.selectedModelData || !this.selectedModelData.visualGroups)
        return [];
      return [...this.selectedModelData.visualGroups].sort((a, b) => {
        const orderA = a.order || 1;
        const orderB = b.order || 1;
        return orderA - orderB;
      });
    },
    /** Raíz del formulario activo (V2 o legado) para no usar document.getElementById global con varios embeds. */
    getFormRootEl() {
      const formRef = this.$refs.formV2;
      if (formRef && formRef.$el) return formRef.$el;
      const legacy = this.$refs.legacyForm;
      if (legacy && legacy.$el) return legacy.$el;
      return null;
    },
    queryFormFieldById(fieldId) {
      const root = this.getFormRootEl();
      if (!root || !fieldId) return null;
      try {
        if (typeof CSS !== "undefined" && CSS.escape) {
          return root.querySelector(`#${CSS.escape(fieldId)}`);
        }
      } catch (e) {
        /* ignore */
      }
      return root.querySelector(`[id="${String(fieldId).replace(/"/g, "")}"]`);
    },
    onInput(formData) {
      // IMPORTANTE: Usar debounce para procesar solo el evento más reciente
      // Esto previene que eventos antiguos sobrescriban eventos nuevos
      // IMPORTANTE: Si el evento tiene un campo explícitamente vacío, procesarlo INMEDIATAMENTE
      // sin debounce para respetar la intención del usuario de borrar el campo
      const hasExplicitEmptyField = Object.keys(formData).some(key => {
        const newValue = formData[key];
        const existingValue = this.form[key];
        return (newValue === '' || newValue === null || newValue === undefined) && 
               existingValue !== '' && existingValue !== null && existingValue !== undefined;
      });
      
      // IMPORTANTE: Siempre usar debounce para procesar solo el evento más reciente
      // Esto previene que eventos antiguos sobrescriban eventos nuevos
      this.pendingInputEvent = formData;
      
      // Limpiar timer anterior si existe
      if (this.inputDebounceTimer) {
        clearTimeout(this.inputDebounceTimer);
      }
      
      // Procesar el evento más reciente después de un delay
      // Usar un delay más largo (100ms) para asegurar que capturamos el evento correcto
      // que viene después de que el watcher de value se dispare
      this.inputDebounceTimer = setTimeout(() => {
        if (this.pendingInputEvent) {
          this.processInputEvent(this.pendingInputEvent);
          this.pendingInputEvent = null;
        }
        this.inputDebounceTimer = null;
      }, 100); // 100ms de debounce para capturar el evento más reciente
    },
    
    processInputEvent(formData) {
      const onInputStartTime = performance.now();
      const callId = Math.random().toString(36).substr(2, 9);
      this.onInputCallCount++;
      // CRÍTICO: Reemplazar this.form en UNA sola asignación para que el watch de FormV2
      // se dispare una sola vez con el form completo (incl. group_location.city = '').
      // Si hacemos $set por cada key, el watch puede dispararse a mitad y recibir form a medias.
      const newForm = JSON.parse(JSON.stringify(formData));
      Object.keys(this.form || {}).forEach(key => {
        if (!(key in newForm)) newForm[key] = this.form[key];
      });
      this.form = newForm;
      // Oferta NC: usar newForm (incluye group_*.client de visualGroups).
      this.maybeAskNotaComercialOnContactSelect(newForm);
      // Resto de la lógica en nextTick para que el watch de FormV2 reciba el form ya actualizado (con city = '', etc.)
      this.$nextTick(() => {
        this._processInputEventRest(formData, onInputStartTime);
      });
    },
    _processInputEventRest(formData, onInputStartTime) {
      const callId = Math.random().toString(36).substr(2, 9);
      // Crear un snapshot del formData para detectar cambios reales
      const normalizedFormData = {};
      Object.keys(formData).sort().forEach(key => {
        if (!key.startsWith('raw_') && key !== 'updatedAt' && key !== 'updatedBy') normalizedFormData[key] = formData[key];
      });
      const formDataSnapshot = JSON.stringify(normalizedFormData);
      const hasExplicitEmptyField = Object.keys(formData).some(key => {
        const newValue = formData[key];
        const existingValue = this.form[key];
        return (newValue === '' || newValue === null || newValue === undefined) && existingValue !== '' && existingValue !== null && existingValue !== undefined;
      });
      
      // PROTECCIÓN: Si ya estamos procesando un input, ignorar
      if (this.isProcessingInput) {
        return;
      }
      
      // PROTECCIÓN: Si el formData es igual al último procesado, ignorar
      // EXCEPCIÓN: Si hay un campo explícitamente vacío, procesar de todas formas
      // EXCEPCIÓN ADICIONAL: Si formData tiene un valor que es diferente al actual en this.form,
      // procesar de todas formas (puede ser un evento que llegó tarde pero es el correcto)
      // IMPORTANTE: Verificar ANTES de actualizar this.form para detectar cambios reales
      const hasDifferentValue = Object.keys(formData).some(key => {
        const newValue = formData[key];
        const currentValue = this.form[key];
        return newValue !== currentValue;
      });
      
      if (this.lastFormDataSnapshot === formDataSnapshot && !hasExplicitEmptyField && !hasDifferentValue) {
        return;
      }
      
      // PROTECCIÓN ADICIONAL: Si se ejecutó muy recientemente (menos de 50ms), ignorar
      // EXCEPCIÓN: Si hay un campo explícitamente vacío o valores diferentes, procesar de todas formas
      // Esto previene ejecuciones en cascada causadas por múltiples watchers
      if (this.lastOnInputTime && (onInputStartTime - this.lastOnInputTime) < 50 && !hasExplicitEmptyField && !hasDifferentValue) {
        // IMPORTANTE: Actualizar lastOnInputTime incluso cuando se bloquea para que la protección temporal funcione correctamente
        this.lastOnInputTime = onInputStartTime;
        return;
      }
      
      // PROTECCIÓN ADICIONAL: Si el formData es muy similar (solo diferencias menores), ignorar
      // Comparar solo los campos importantes para evitar ejecuciones redundantes
      // EXCEPCIÓN: Si hay un campo explícitamente vacío, procesar de todas formas
      if (this.lastFormDataSnapshot && !hasExplicitEmptyField) {
        try {
          const lastData = JSON.parse(this.lastFormDataSnapshot);
          const currentData = JSON.parse(formDataSnapshot);
          
          // Comparar solo campos clave (excluyendo campos calculados que cambian frecuentemente)
          const keysToCompare = Object.keys(currentData).filter(key => 
            !key.startsWith('raw_') && 
            key !== 'updatedAt' && 
            key !== 'updatedBy'
          );
          
          let hasSignificantChanges = false;
          for (const key of keysToCompare) {
            if (JSON.stringify(lastData[key]) !== JSON.stringify(currentData[key])) {
              hasSignificantChanges = true;
              break;
            }
          }
          
          if (!hasSignificantChanges) {
            // Actualizar lastOnInputTime para que la protección temporal funcione
            this.lastOnInputTime = onInputStartTime;
            return;
          }
        } catch (e) {
          // Si hay error al comparar, continuar con el procesamiento normal
        }
      }
      
      // Marcar que estamos procesando
      this.isProcessingInput = true;
      this.lastFormDataSnapshot = formDataSnapshot;
      this.lastOnInputTime = onInputStartTime;
      
      // IMPORTANTE: Marcar que estamos haciendo cambios automáticos para evitar que disparen el watcher
      // Esto previene que las actualizaciones dentro de onInput causen más ejecuciones de onInput
      if (this.$refs.autoSaveForm && this.$refs.autoSaveForm.isAutomaticChange !== undefined) {
        this.$refs.autoSaveForm.isAutomaticChange = true;
      }
      
      // Guardar en el historial (mantener solo los últimos 5)
      this.onInputCallHistory.push({
        callId,
        tiempo: onInputStartTime,
        keysCount: Object.keys(formData).length,
        stackTrace: new Error().stack.split('\n').slice(1, 4).map(line => line.trim())
      });
      if (this.onInputCallHistory.length > 5) {
        this.onInputCallHistory.shift();
      }
      
      // IMPORTANTE: Si el formData entrante tiene significativamente menos campos que this.form,
      // es probable que venga de un Form anidado. En ese caso, hacer merge en lugar de reemplazar.
      const formDataKeysCount = Object.keys(formData).length;
      const thisFormKeysCount = Object.keys(this.form || {}).length;
      const isPartialUpdate = thisFormKeysCount > 0 && formDataKeysCount < thisFormKeysCount * 0.7; // Si tiene menos del 70% de los campos, es parcial
      
      if (isPartialUpdate) {
        // Es una actualización parcial (probablemente de un Form anidado)
        // Hacer merge inteligente que preserve todos los campos del nivel raíz que no están en formData
        Object.keys(this.form).forEach(key => {
          if (!(key in formData)) {
            // El campo no está en formData, agregarlo al formData para preservarlo
            formData[key] = this.form[key];
          } else if (formData[key] === null || formData[key] === undefined || formData[key] === '') {
            // IMPORTANTE: Si el campo está explícitamente en formData (aunque esté vacío),
            // NO preservar el valor existente. Esto respeta la intención del usuario de borrar el campo.
            // Solo preservar si el campo NO está en formData (actualización parcial real)
            // No hacer nada aquí - dejar el valor vacío como está
          }
        });
      }
      
      // IMPORTANTE: Preservar campos calculados (raw_*) cuando se actualiza el formData
      // Esto evita que se pierdan cuando el Form anidado emite input sin estos campos
      const preservedCalculatedFields = {};
      const calculatedFieldKeys = ['raw_totalqty', 'raw_totalgrossweight', 'raw_totalprice', 'raw_totalqtycont', 'raw_totalpricecont'];
      
      // IMPORTANTE: Preservar total_invoicesupplier si está en el nivel raíz
      // Este campo puede estar en un grupo visual pero debe guardarse en el nivel raíz
      const preservedTotalInvoiceSupplier = this.form && this.form.total_invoicesupplier !== undefined 
        ? this.form.total_invoicesupplier 
        : null;
      
     
      
      // Preservar campos calculados del form actual si tienen valor
      if (this.form) {
        calculatedFieldKeys.forEach(key => {
          if (this.form[key] !== null && 
              this.form[key] !== undefined && 
              this.form[key] !== '') {
            preservedCalculatedFields[key] = this.form[key];
          }
        });
      }
      
      // IMPORTANTE: Preservar TODOS los campos del nivel raíz que no están en formData
      // Esto es necesario porque Form anidados pueden emitir solo sus campos locales
      const preservedRootFields = {};
      if (this.form) {
        Object.keys(this.form).forEach(key => {
          // Solo preservar campos que no están en formData o que están vacíos en formData pero tienen valor en this.form
          // IMPORTANTE: Si el campo está en formData con null, pero tiene un valor en this.form, preservarlo
          // Esto previene que Form anidados sobrescriban valores del Form principal con null
          if (!(key in formData) || (formData[key] === null || formData[key] === undefined || formData[key] === '') && 
              this.form[key] !== null && this.form[key] !== undefined && this.form[key] !== '') {
            // No preservar campos calculados (ya se manejan por separado)
            if (!calculatedFieldKeys.includes(key)) {
              preservedRootFields[key] = this.form[key];
            }
          }
        });
      }
      
      // IMPORTANTE: Si formData tiene un campo con null pero ese campo NO debería estar en ese formData
      // (porque viene de un Form anidado), eliminarlo del formData para evitar que sobrescriba valores
      // Esto es especialmente importante para campos que están en el nivel raíz del Form principal
      // pero que un Form anidado incluye con null en su formData
      if (!isPartialUpdate) {
        // Solo para actualizaciones completas, verificar si hay campos con null que no deberían estar
        Object.keys(formData).forEach(key => {
          // Si el campo está en formData con null, pero NO está en this.form (o está con null en ambos),
          // y el campo NO es parte de un grupo anidado, podría ser un campo del nivel raíz que un Form anidado
          // está intentando sobrescribir con null
          if (formData[key] === null && 
              (this.form[key] === null || this.form[key] === undefined) &&
              !calculatedFieldKeys.includes(key)) {
            // Verificar si este campo debería estar en el nivel raíz según modelFields
            const isRootLevelField = this.modelFields && this.modelFields.some(f => f.key === key && f.type !== 'group');
            if (isRootLevelField) {
              // Este campo debería estar en el nivel raíz, pero está con null
              // Eliminarlo del formData para evitar que sobrescriba valores
              delete formData[key];
            }
          }
        });
      }
      
      // Buscar valores de campos calculados en el DOM antes de actualizar
      const domCalculatedFields = {};
      calculatedFieldKeys.forEach(key => {
        // Buscar el campo en el DOM - puede tener diferentes formatos de ID
        // Para campos dentro de grupos anidados, el ID puede ser: group_proform-productandprice-1-raw_totalqty
        const possibleIds = [
          key, // ID simple (nivel raíz)
          `group_proform-${key}`, // Con prefijo de grupo
          `group_proform-productandprice-1-${key}`, // Con prefijo completo del grupo anidado
        ];
        
        for (const id of possibleIds) {
          const fieldElement = this.queryFormFieldById(id);
          if (fieldElement && fieldElement.classList.contains("calculated-field")) {
            const domValue = fieldElement.value;
            if (domValue && domValue !== '') {
              domCalculatedFields[key] = isNaN(domValue) ? domValue : Number(domValue);
            }
            break;
          }
        }
      });
      
      // IMPORTANTE: Preservar total_invoicesupplier del grupo visual si está ahí pero no en el nivel raíz
      // Esto es necesario porque applyVisualRules() puede mover el campo al grupo visual
      if (formData.group_invoicesupplier && formData.group_invoicesupplier.total_invoicesupplier !== undefined) {
        const valorEnGroup = formData.group_invoicesupplier.total_invoicesupplier;
        const valorEnRaiz = formData.total_invoicesupplier;
        
        // Si el valor está en el grupo pero no en el nivel raíz, o si el valor del grupo es diferente, usar el del grupo
        if (valorEnGroup !== undefined && valorEnGroup !== null && valorEnGroup !== '') {
          if (valorEnRaiz === undefined || valorEnRaiz === null || valorEnRaiz === '' || valorEnRaiz !== valorEnGroup) {
            formData.total_invoicesupplier = valorEnGroup;
           
          }
        }
      }
      
      // OPTIMIZACIÓN: Acumular todos los cambios y aplicarlos de una vez para evitar múltiples disparos del watcher
      // Esto reduce significativamente el número de ejecuciones de onInput
      const changesToApply = {};
      let hasChanges = false;
      
      // IMPORTANTE: Hacer merge inteligente que preserve valores existentes cuando el nuevo es null/empty
      // Actualizar el form con el nuevo formData, pero preservar campos del nivel raíz que tienen valor
      Object.keys(formData).forEach(key => {
        const newValue = formData[key];
        const existingValue = this.form[key];
        
        // IMPORTANTE: Si el campo está explícitamente en formData (aunque esté vacío),
        // respetar ese valor. Esto significa que el usuario intencionalmente lo estableció (incluso si es vacío).
        // Solo preservar valores cuando el campo NO está en formData (actualización parcial real).
        // Si el nuevo valor es null/undefined/empty pero el existente tiene valor, y el campo está en formData,
        // actualizar con el valor vacío (el usuario lo borró explícitamente)
        if ((newValue === null || newValue === undefined || newValue === '') && 
            existingValue !== null && existingValue !== undefined && existingValue !== '' &&
            !calculatedFieldKeys.includes(key)) {
          // El campo está explícitamente en formData con valor vacío - respetar la intención del usuario
          // Actualizar con el valor vacío
          if (existingValue !== newValue) {
            changesToApply[key] = newValue;
            hasChanges = true;
          }
        } else if (newValue !== null && newValue !== undefined && newValue !== '') {
          // Solo actualizar si el nuevo valor NO es null/undefined/empty Y es diferente al existente
          if (existingValue !== newValue) {
            changesToApply[key] = newValue;
            hasChanges = true;
          }
        } else {
          // Si ambos son null/empty, no hacer nada (mantener el estado actual)
          // PERO: Si el campo existe en preservedRootFields, restaurarlo
          if (preservedRootFields[key] !== undefined && this.form[key] !== preservedRootFields[key]) {
            changesToApply[key] = preservedRootFields[key];
            hasChanges = true;
          }
        }
      });
      
      // Restaurar campos preservados del nivel raíz (esto tiene prioridad)
      // IMPORTANTE: NO restaurar si el campo está explícitamente en formData con valor vacío
      // (eso significa que el usuario lo borró intencionalmente)
      Object.keys(preservedRootFields).forEach(key => {
        // Si el campo está explícitamente en formData (incluso si está vacío), NO restaurar
        // porque eso significa que el usuario intencionalmente lo estableció (o lo borró)
        if (key in formData) {
          return; // No restaurar este campo
        }
        
        // Solo restaurar si el campo NO está en formData (actualización parcial real)
        if (this.form[key] !== preservedRootFields[key]) {
          changesToApply[key] = preservedRootFields[key];
          hasChanges = true;
        }
      });
      
      // Restaurar campos calculados: priorizar DOM > preservado > nuevo valor
      calculatedFieldKeys.forEach(key => {
        const newValue = formData[key];
        const preservedValue = preservedCalculatedFields[key];
        const domValue = domCalculatedFields[key];
        let valueToSet = null;
        
        if (domValue !== undefined) {
          // El DOM tiene el valor más reciente y confiable
          valueToSet = domValue;
        } else if (preservedValue !== undefined) {
          // Si no hay valor en el DOM, usar el preservado si el nuevo está vacío
          if (newValue === null || newValue === undefined || newValue === '') {
            valueToSet = preservedValue;
          } else {
            // Si ambos tienen valores, usar el preservado (más confiable que el nuevo)
            valueToSet = preservedValue;
          }
        }
        
        if (valueToSet !== null && this.form[key] !== valueToSet) {
          changesToApply[key] = valueToSet;
          hasChanges = true;
        }
      });
      
      // Aplicar todos los cambios de una vez para evitar múltiples disparos del watcher
      if (hasChanges) {
        // Marcar como cambio automático antes de aplicar
        if (this.$refs.autoSaveForm && this.$refs.autoSaveForm.isAutomaticChange !== undefined) {
          this.$refs.autoSaveForm.isAutomaticChange = true;
        }
        
        Object.keys(changesToApply).forEach(key => {
          this.$set(this.form, key, changesToApply[key]);
        });
      }
      
      // IMPORTANTE: Restaurar total_invoicesupplier en el nivel raíz si fue preservado
      // Este campo debe estar siempre en el nivel raíz, no solo en el grupo visual
      // Agregarlo a changesToApply para que se aplique junto con los demás cambios
      if (preservedTotalInvoiceSupplier !== null && preservedTotalInvoiceSupplier !== undefined && preservedTotalInvoiceSupplier !== '') {
        // Priorizar el valor del nivel raíz si existe, sino usar el preservado
        const valorEnRaiz = formData.total_invoicesupplier;
        const valorFinal = valorEnRaiz !== undefined && valorEnRaiz !== null && valorEnRaiz !== '' 
          ? valorEnRaiz 
          : preservedTotalInvoiceSupplier;
        
        // Solo agregar si el valor es diferente
        if (this.form.total_invoicesupplier !== valorFinal) {
          changesToApply['total_invoicesupplier'] = valorFinal;
          hasChanges = true;
          
        }
      } else if (formData.total_invoicesupplier !== undefined && formData.total_invoicesupplier !== null && formData.total_invoicesupplier !== '') {
        // Si no había valor preservado pero el formData tiene el valor en el nivel raíz, usarlo
        if (this.form.total_invoicesupplier !== formData.total_invoicesupplier) {
          changesToApply['total_invoicesupplier'] = formData.total_invoicesupplier;
          hasChanges = true;
          
        }
      }
      
      // Aplicar todos los cambios de una vez (incluyendo total_invoicesupplier si se agregó)
      if (hasChanges) {
        // Marcar como cambio automático antes de aplicar
        if (this.$refs.autoSaveForm && this.$refs.autoSaveForm.isAutomaticChange !== undefined) {
          this.$refs.autoSaveForm.isAutomaticChange = true;
        }
        
        Object.keys(changesToApply).forEach(key => {
          this.$set(this.form, key, changesToApply[key]);
        });
      }
      
      const onInputEndTime = performance.now();
      
      // Desmarcar que terminamos de procesar
      this.isProcessingInput = false;
      
      // IMPORTANTE: Desmarcar cambios automáticos después de que todas las actualizaciones se completen
      // Esto permite que el watcher vuelva a funcionar normalmente
      // Usar un timeout más largo para asegurar que todas las actualizaciones se completen
      setTimeout(() => {
        // Resetear la bandera después de que Vue procese todos los cambios
        this.isProcessingInput = false;
        
        // Desmarcar cambios automáticos en AutoSaveForm
        if (this.$refs.autoSaveForm && this.$refs.autoSaveForm.isAutomaticChange !== undefined) {
          this.$refs.autoSaveForm.isAutomaticChange = false;
        }
      }, 100); // 100ms de delay para asegurar que todas las actualizaciones se completen
    },
    async onButtonClick(val) {
      const dataToSave = this.processFormData(this.form);
      await wencesApi
        .post(`/api/gotrading/dynamic/${this.selectedModel}/preload`, {
          action: val.value,
          item: dataToSave,
        })
        .then((response) => {
          this.form = response.data.data;
          this.fetchItemPreload();
        });
    },
    async fetchModels() {
      const { data } = await wencesApi.get("/api/gotrading/model-definitions");
      this.models = data;

      // Cargar todas las definiciones de modelos
      const modelDefinitions = {};
      for (const model of data) {
        modelDefinitions[model.modelName] = model;
      }
      this.modelDefinitions = modelDefinitions;
    },
    async probeAutonomousShell() {
      const modelName = this.routeModelName;
      if (!modelName) return false;
      try {
        const mode = this.isAddMode ? "add" : "edit";
        const params = { mode };
        if (!this.isAddMode && this.routeItemId) {
          params.itemId = this.routeItemId;
        }
        const { data } = await wencesApi.get(
          `/api/gotrading/dynamic/${modelName}/form-schema`,
          { params }
        );
        if (
          isAutonomousDynamicForm(data.modelName, mode, {
            customFormKey: data.meta?.customFormKey,
            trustCustomFormKey: true,
          })
        ) {
          this.useAutonomousShell = true;
          return true;
        }
      } catch (_e) {
        /* fallback a flujo FormV2 */
      }
      this.useAutonomousShell = false;
      return false;
    },
    async fetchFields() {
      this.modelFields = [];
      this.customFormComponent = null;
      if (!this.selectedModel) {
        console.log("NO SE HA CARGADO EL MODELO");
        return;
      }
      console.log("CARGANDO CAMPO", this.selectedModel);
      this.isLoadingFormSchema = true;
      try {
        const mode = this.isAddMode ? "add" : "edit";
        const params = { mode };
        if (!this.isAddMode && this.routeItemId) {
          params.itemId = this.routeItemId;
        }
        const { data } = await wencesApi.get(
          `/api/gotrading/dynamic/${this.selectedModel}/form-schema`,
          { params }
        );

        this.formMeta = data.meta || {};
        if (
          isAutonomousDynamicForm(data.modelName, mode, {
            customFormKey: this.formMeta?.customFormKey,
            trustCustomFormKey: true,
          })
        ) {
          this.useAutonomousShell = true;
          this.isLoadingFormSchema = false;
          return;
        }
        this.useAutonomousShell = false;

        const modelFromList = this.models.find(
          (m) => m.modelName.toLowerCase() === this.selectedModel.toLowerCase()
        );
        this.selectedModelData = {
          ...(modelFromList || {}),
          modelName: data.modelName,
          label: data.label,
          autoSave: data.autoSave,
          visualGroups: data.visualGroups,
          fields: data.fields,
        };
        this.modelFields = data.fields;
        this.formSchemaDefaults = data.formDefaults || {};

        await this.resolveCustomFormComponent();

        if (this.isAddMode) {
          this.resetForm();
          this.applyFormSchemaDefaults();
          this.applyRouteQueryPrefill();
        } else {
          this.fetchItem(this.routeItemId);
        }
      } catch (error) {
        console.error("DynamicModelEdit: form-schema failed, using model-definitions", error);
        const model = this.models.find(
          (m) => m.modelName.toLowerCase() === this.selectedModel.toLowerCase()
        );
        if (model) {
          this.selectedModelData = model;
          this.modelFields = model.fields;
          await this.resolveCustomFormComponent();
          if (this.isAddMode) {
            this.resetForm();
            this.applyRouteQueryPrefill();
          } else {
            this.fetchItem(this.routeItemId);
          }
        }
      } finally {
        this.isLoadingFormSchema = false;
      }
    },
    applyFormSchemaDefaults() {
      if (!this.formSchemaDefaults || typeof this.formSchemaDefaults !== "object") {
        return;
      }
      Object.keys(this.formSchemaDefaults).forEach((key) => {
        this.$set(this.form, key, this.formSchemaDefaults[key]);
      });
    },
    async resolveCustomFormComponent() {
      const mode = this.isAddMode ? "add" : "edit";
      this.customFormComponent = await resolveDynamicFormComponent(
        this.selectedModel,
        mode,
        {
          customFormKey: this.formMeta?.customFormKey || null,
          trustCustomFormKey: true,
        }
      );
    },
    applyRouteQueryPrefill() {
      const q = this.$route.query || {};
      if (!this.form || typeof this.form !== "object") return;
      if (q.startDate != null && String(q.startDate).trim() !== "") {
        const v = String(q.startDate).trim();
        if (Object.prototype.hasOwnProperty.call(this.form, "startDate")) {
          this.$set(this.form, "startDate", v);
        }
      }
      if (q.endDate != null && String(q.endDate).trim() !== "") {
        const v = String(q.endDate).trim();
        if (Object.prototype.hasOwnProperty.call(this.form, "endDate")) {
          this.$set(this.form, "endDate", v);
        }
      }
    },
    resetForm() {
      const defaultForm = {};
      this.modelFields.forEach((field) => {
        if (field.isArray) {
          defaultForm[field.key] = [];
        } else if (field.type === "group") {
          defaultForm[field.key] = {};
          if (field.fields) {
            field.fields.forEach((subField) => {
              defaultForm[field.key][subField.key] =
                subField.type === "number" ? 0 : "";
            });
          }
        } else {
          defaultForm[field.key] = field.type === "number" ? 0 : "";
        }
      });
      if (this.selectedModelData?.visualGroups?.length) {
        this.selectedModelData.visualGroups.forEach((group) => {
          const groupKey = `group_${group.name}`;
          defaultForm[groupKey] = {};
          this.modelFields.forEach((field) => {
            if (field.visualGroup === group.name && defaultForm[field.key] !== undefined) {
              defaultForm[groupKey][field.key] = defaultForm[field.key];
              copyTranslatableSiblingsInto(defaultForm[groupKey], defaultForm, field);
            } else if (field.visualGroup === group.name) {
              defaultForm[groupKey][field.key] = field.isArray ? [] : (field.type === "group" ? {} : (field.type === "number" ? 0 : ""));
              copyTranslatableSiblingsInto(defaultForm[groupKey], defaultForm, field);
            }
          });
        });
      }
      this.form = defaultForm;
      this.item = defaultForm;
    },
    async fetchItemPreload() {
      // Ya no necesitamos transformar fechas aquí porque vienen transformadas del backend

      if(!this.modelFields){
        await this.fetchFields();
      }

      let formData = { ...this.form };
      
      if(formData.action && formData.item){
        formData = formData.item;
      }



      // Procesar visual groups si existen
      if (
        this.selectedModelData?.visualGroups &&
        this.selectedModelData.visualGroups.length > 0
      ) {
        this.selectedModelData.visualGroups.forEach((group) => {
          const groupKey = `group_${group.name}`;
          formData[groupKey] = {};

          this.modelFields.forEach((field) => {
            if (
              field.visualGroup === group.name &&
              formData[field.key] !== undefined
            ) {
              // Las fechas ya vienen transformadas del backend, solo copiarlas
              formData[groupKey][field.key] = formData[field.key];
              // Traducciones: name_es / name_en viven en root; el form las lee bajo group_*
              copyTranslatableSiblingsInto(formData[groupKey], formData, field);
            }
          });
        });
      }

      this.form = formData;
      this.item = formData;
      // El Breadcrumb lee de acá; sin esto muestra el `#<últimos 6 del id>` de respaldo.
      setCurrentItemLabel(formData._id, buildItemDisplayName(formData));
    },
    /**
     * Completa los campos que este cliente (o la referencia que sea) usa habitualmente,
     * según el historial del modelo. Solo rellena campos vacíos y no guarda nada.
     */
    async applyReferenceFieldSuggestions() {
      const rule = this.referenceSuggestionRule;
      if (!rule || !this.form) return;
      const pending = rule.targetFields.filter((key) =>
        isEmptyFormValue(this.form[key])
      );
      if (!pending.length) return;
      try {
        const data = await fetchReferenceFieldSuggestions({
          modelName: this.selectedModel,
          sourceField: rule.sourceField,
          sourceValue: this.form[rule.sourceField],
          targetFields: pending,
        });
        Object.keys(data || {}).forEach((key) => {
          const suggestion = data[key];
          if (!suggestion || isEmptyFormValue(suggestion.value)) return;
          if (!isEmptyFormValue(this.form[key])) return;
          this.$set(this.form, key, suggestion.value);
        });
      } catch (e) {
        /* La sugerencia es opcional: si falla, el campo queda vacío. */
      }
    },
    async fetchItem(itemId) {
      try {
        // Las fechas ahora se manejan como texto simple, no necesitan transformación de zona horaria
        const { data } = await wencesApi.get(
          `/api/gotrading/dynamic/${this.selectedModel}/${itemId}`
        );

        if (String(itemId) !== String(this.routeItemId)) {
          return;
        }

        const formData = { ...data };
        this.form = formData;

        // Recién con el ítem cargado tiene sentido sugerir por cambio de cliente:
        // al abrir la ficha no se toca nada de lo ya guardado.
        this.$nextTick(() => {
          this.referenceSuggestionsArmed = true;
        });

        // Procesar grupos visuales
        this.fetchItemPreload();
      } catch (error) {
        console.error("Error loading item:", error);
      }
    },
    async fetchLists() {
      const { data } = await wencesApi.get("/api/gotrading/lists");
      this.lists = data;
    },
    async acquireLock() {
      try {
        await wencesApi.post(
          `/api/gotrading/edit-lock/${this.selectedModel}/${this.routeItemId}/acquire`
        );
        // Iniciar el intervalo para mantener el bloqueo activo
        this.lockInterval = setInterval(() => {
          this.updateLock();
        }, 30000); // Actualizar cada 30 segundos
      } catch (error) {
        if (error.response && error.response.status === 409) {
          const lock = error.response.data.lock;
          const timeLeft = Math.floor(lock.timeLeft);
          const lastActivity = new Date(lock.lastActivity).toLocaleTimeString();

          await Swal.fire({
            title: "Otro usuario esta editando este registro",
            html: `
              <div style="text-align: left;">
                <p><strong>Usuario:</strong> ${lock.userName}</p>
                <p><strong>Última actividad:</strong> ${lastActivity}</p>
              </div>
            `,
            icon: "warning",
            confirmButtonText: "Entendido",
          });

          if (!this.embedded) {
            this.$router.push({
              name: "DynamicModelDetail",
              params: {
                modelName: this.selectedModel,
                itemId: this.routeItemId,
              },
            });
          }
        } else {
          console.error("Error al adquirir bloqueo:", error);
          await Swal.fire({
            title: "Error",
            text: "No se pudo adquirir el bloqueo de edición",
            icon: "error",
            confirmButtonText: "Entendido",
          });
        }
      }
    },

    async updateLock() {
      try {
        await wencesApi.post(
          `/api/gotrading/edit-lock/${this.selectedModel}/${this.routeItemId}/activity`
        );
      } catch (error) {
        console.error("Error al actualizar bloqueo:", error);
        // Si hay error, intentar liberar el bloqueo
        this.releaseLock();
      }
    },

    async releaseLock() {
      try {
        await wencesApi.post(
          `/api/gotrading/edit-lock/${this.selectedModel}/${this.routeItemId}/release`
        );
        if (this.lockInterval) {
          clearInterval(this.lockInterval);
          this.lockInterval = null;
        }
      } catch (error) {
        console.error("Error al liberar bloqueo:", error);
      }
    },

    /** Modo «Preguntar» del mapeo FindTeu: ofrece activar el seguimiento tras un guardado manual. */
    async promptFindTeuTracking(itemId) {
      await promptFindTeuTrackingOnSave({
        modelName: this.selectedModel,
        itemId,
        t: (key, params) => this.$t(key, params),
      });
    },
    async onSubmit(form, autosave) {
      const startTime = performance.now();
      const submitId = Math.random().toString(36).substr(2, 9);
      const timestamps = {
        inicio: startTime,
        processFormDataInicio: null,
        processFormDataFin: null,
        apiInicio: null,
        apiFin: null,
        fin: null
      };
      
      try {
        // Gasto sin «Related file»: se pregunta antes de guardar (solo al guardar a mano;
        // el autoguardado no interrumpe). Cancelar deja el formulario como estaba.
        if (
          autosave !== "autosave" &&
          !(await confirmDuntowerExpenseSaveWithoutFile(
            (key, params) => this.$t(key, params),
            this.selectedModel,
            this.getFormForSave()
          ))
        ) {
          return;
        }

        this.isSubmitting = true;

        // Si el autoguardado está dando de alta este registro, esperarlo: si no, el guardado
        // manual sale sin `_id` y pide otra alta (dos documentos y dos correlativos).
        await this.waitForPendingAutoSave();

        const formForSave = this.getFormForSave();
        const formDataAntes = JSON.parse(JSON.stringify(this.form));
        const tiempoAntesProcessFormData = performance.now();

        timestamps.processFormDataInicio = performance.now();
        const dataToSave = this.processFormData(formForSave);
        const manualPayload = { ...dataToSave, saveTrigger: "manual" };
        timestamps.processFormDataFin = performance.now();

        timestamps.apiInicio = performance.now();

        if (this.isAddMode) {
          // El autoguardado pudo haber creado ya el registro: entonces esto es un update.
          const autoSavedId = this.form._id || null;
          const { data } = autoSavedId
            ? await wencesApi.put(
                `/api/gotrading/dynamic/${this.selectedModel}/${autoSavedId}`,
                manualPayload
              )
            : await wencesApi.post(`/api/gotrading/dynamic/${this.selectedModel}`, {
                ...manualPayload,
                _clientCreateKey: this.clientCreateKey,
              });
          timestamps.apiFin = performance.now();
          timestamps.fin = performance.now();
          if (data?._id) {
            this.$set(this.form, "_id", data._id);
            this.item = { ...this.form };
            await this.applyPendingNotaComercialOffer(data._id);
          } else if (this.form?._id) {
            await this.applyPendingNotaComercialOffer(this.form._id);
          }
          if (data?._operationMessages && Array.isArray(data._operationMessages)) {
            for (const msg of data._operationMessages) {
              await Swal.fire({
                icon: msg.type === "success" ? "success" : "info",
                title: msg.type === "success" ? "Éxito" : "Información",
                text: msg.message,
                confirmButtonText: "Entendido",
                confirmButtonColor: msg.type === "success" ? "#28a745" : "#17a2b8",
              });
            }
          }
          if (autosave !== "autosave") {
            await this.promptFindTeuTracking(data?._id || this.form?._id);
          }
          if (autosave !== "autosave" && !this.embedded) {
            if (data?._id) {
              this.$router.push(`/dynamic-model/${this.selectedModel}/view/${data._id}`);
            } else {
              this.$router.push({ name: "DynamicModel", params: { modelName: this.selectedModel } });
            }
          }
        } else {
          const { data } = await wencesApi.put(
            `/api/gotrading/dynamic/${this.selectedModel}/${this.routeItemId}`,
            manualPayload
          );
          timestamps.apiFin = performance.now();
          timestamps.fin = performance.now();
          await this.applyPendingNotaComercialOffer(this.routeItemId);
          if (autosave !== "autosave") {
            await this.promptFindTeuTracking(this.routeItemId);
            if (this.embedded) {
              this.$emit("embedded-save");
            } else {
              await this.releaseLock();
              this.navigateAfterEditDone(this.selectedModel, this.routeItemId);
            }
          }
        }
      } catch (error) {
        console.error("Error saving:", error);
        this.isSubmitting = false;
        await this.$nextTick();

        // Manejar errores de validación del backend
        const errorData = getApiErrorData(error);
        if (errorData) {
          if (isHookValidationError(errorData)) {
            const confirmed = await promptHookValidationForceSave(
              (key, params) => this.$t(key, params),
              errorData.message,
              { modelName: this.selectedModel }
            );

            if (confirmed) {
              this.isSubmitting = true;
              try {
                const dataToSave = this.processFormData(this.getFormForSave());
                if (this.isAddMode) {
                  const autoSavedId = this.form._id || null;
                  const { data } = autoSavedId
                    ? await wencesApi.put(
                        `/api/gotrading/dynamic/${this.selectedModel}/${autoSavedId}`,
                        { ...dataToSave, forceSave: true, saveTrigger: "manual" }
                      )
                    : await wencesApi.post(`/api/gotrading/dynamic/${this.selectedModel}`, {
                        ...dataToSave,
                        forceSave: true,
                        saveTrigger: "manual",
                        _clientCreateKey: this.clientCreateKey,
                      });
                  if (data?._id) {
                    this.$set(this.form, "_id", data._id);
                    await this.applyPendingNotaComercialOffer(data._id);
                  }
                  if (autosave !== "autosave" && data?._id) {
                    await this.promptFindTeuTracking(data._id);
                  }
                  if (autosave !== "autosave" && data?._id && !this.embedded) {
                    this.$router.push(`/dynamic-model/${this.selectedModel}/view/${data._id}`);
                  }
                } else {
                  await wencesApi.put(
                    `/api/gotrading/dynamic/${this.selectedModel}/${this.routeItemId}`,
                    { ...dataToSave, forceSave: true, saveTrigger: "manual" }
                  );
                  await this.applyPendingNotaComercialOffer(this.routeItemId);
                  if (autosave !== "autosave") {
                    await this.promptFindTeuTracking(this.routeItemId);
                    if (this.embedded) {
                      this.$emit("embedded-save");
                    } else {
                      await this.releaseLock();
                      this.navigateAfterEditDone(this.selectedModel, this.routeItemId);
                    }
                  }
                }
              } catch (retryError) {
                console.error("Error forceSave:", retryError);
                await showForceSaveRetryError(
                  (key, params) => this.$t(key, params),
                  retryError
                );
              } finally {
                this.isSubmitting = false;
              }
            }
          } else if (errorData.error) {
            // Otros errores del backend
            Swal.fire({
              icon: "error",
              title: "Error",
              text: errorData.error,
              confirmButtonText: "OK",
            });
          } else {
            // Error genérico
            Swal.fire({
              icon: "error",
              title: "Error",
              text: "Ocurrió un error al guardar. Por favor, inténtalo de nuevo.",
              confirmButtonText: "OK",
            });
          }
        } else if (error.message) {
          // Error de red o JavaScript
          Swal.fire({
            icon: "error",
            title: "Error de Conexión",
            text: error.message,
            confirmButtonText: "OK",
          });
        } else {
          // Error desconocido
          Swal.fire({
            icon: "error",
            title: "Error Desconocido",
            text: "Ocurrió un error inesperado. Por favor, inténtalo de nuevo.",
            confirmButtonText: "OK",
          });
        }
      } finally {
        this.isSubmitting = false;
      }
    },

    getFormForSave() {
      if (
        this.useFormV2 &&
        this.$refs.formV2 &&
        typeof this.$refs.formV2.getState === "function"
      ) {
        const state = this.$refs.formV2.getState();
        return JSON.parse(JSON.stringify(state));
      }
      return this.form;
    },
    processFormData(form) {
      // PROTECCIÓN: Si ya estamos guardando, evitar ejecuciones redundantes desde AutoSaveForm
      const stackTrace = new Error().stack;
      const isFromAutoSave = stackTrace.includes('AutoSaveForm');
      const isFromOnSubmit = stackTrace.includes('onSubmit');
      
      // Si viene de AutoSaveForm y estamos guardando, ignorar para evitar ejecuciones redundantes
      if (isFromAutoSave && this.isSubmitting) {
        // Devolver una copia del form sin procesar para evitar errores
        return { ...form };
      }
      
      const processStartTime = performance.now();
      const processTimestamps = {
        inicio: processStartTime,
        copiaForm: null,
        visualGroups: null,
        preservacion: null,
        processFields: null,
        calculatedFields: null,
        fallback: null,
        fin: null
      };

      // Obtener stack trace completo para identificar quién llama
      const caller = stackTrace.split('\n').slice(1, 6).map(line => line.trim());
      const callId = Math.random().toString(36).substr(2, 9);

      const dataToSave = { ...form };
      processTimestamps.copiaForm = performance.now();
      /** Claves que se mueven de grupos visuales al raíz; no se eliminan si están vacías, se envían como null */
      const movedToRootFromVisualGroups = new Set();

      // Función para verificar si un valor está vacío
      const isEmpty = (value) => {
        if (value === null || value === undefined) return true;
        if (value === "") return true;
        if (Array.isArray(value) && value.length === 0) return true;
        if (typeof value === "object" && Object.keys(value).length === 0)
          return true;
        return false;
      };

      // Solo ocultos se excluyen del envío; los readonly/calculados sí se guardan
      const isFieldHidden = (field) => !!field.hide;

      // Función para limpiar un objeto manteniendo solo los campos definidos y no vacíos
      const cleanObject = (obj, fields) => {
        const cleaned = {};
        fields.forEach((field) => {
          if (isFieldHidden(field)) return;

          const value = obj[field.key];

          if (field.isArray && (value == null || value.length === 0)) {
            cleaned[field.key] = null;
          } else {
            cleaned[field.key] = value;
          }
        });
        
        return cleaned;
      };

      // Procesar los campos según su tipo
      const processFields = (data, fields) => {
        Object.keys(data).forEach((key) => {
          // Encontrar la definición del campo
          const fieldDef = fields.find((f) => f.key === key);

          if (fieldDef) {
            // Si es un campo de tipo reference, solo mantener el _id si no está vacío
            if (
              fieldDef.type === "reference" &&
              data[key] &&
              typeof data[key] === "object"
            ) {
              // Si no tiene _id o el _id está vacío o el objeto tiene name: 'CARGANDO...', establecer como null
              if (!data[key]._id || data[key]._id === '' || data[key]._id === null || data[key].name === 'CARGANDO...' || data[key].name === 'Cargando...') {
                data[key] = null;
              } else {
                data[key] = data[key]._id;
              }
            }
            // Si es un campo de tipo group
            else if (
              fieldDef.type === "group" &&
              data[key] &&
              typeof data[key] === "object"
            ) {
              // Si es un array de grupos (isArray: true)
              if (fieldDef.isArray && Array.isArray(data[key])) {
                data[key] = data[key]
                  .map((item, index) => {
                    if (item && typeof item === "object") {
                      // Preservar el _id si existe
                      const _id = item._id;
                      // Limpiar cada grupo del array
                      const cleanedItem = cleanObject(item, fieldDef.fields);
                      
                      // IMPORTANTE: Si el item NO tiene _id, es un item nuevo y debe enviarse
                      // incluso si está vacío, para que el backend pueda crearlo
                      const isNewItem = !_id;
                      
                      // Solo mantener items que tengan al menos un campo no vacío O sean items nuevos
                      if (Object.keys(cleanedItem).length > 0 || isNewItem) {
                        // Procesar los campos anidados de cada grupo
                        const processedItem = processFields(
                          cleanedItem,
                          fieldDef.fields
                        );
                        // Restaurar el _id si existía
                        if (_id) {
                          processedItem._id = _id;
                        }

                        return processedItem;
                      } else {
                      }
                    }
                    return null;
                  })
                  .filter((item) => item !== null); // Eliminar items nulos
                  
              } else {
                // Si es un grupo simple (no array)
                // Preservar el _id si existe
                const _id = data[key]._id;
                data[key] = cleanObject(data[key], fieldDef.fields);
                // Solo procesar si hay campos no vacíos
                if (Object.keys(data[key]).length > 0) {
                  const processedGroup = processFields(
                    data[key],
                    fieldDef.fields
                  );
                  // Restaurar el _id si existía
                  if (_id) {
                    processedGroup._id = _id;
                  }
                  data[key] = processedGroup;
                }
              }
            }
            // Si es un array de otros tipos
            else if (Array.isArray(data[key])) {
              data[key] = data[key]
                .map((item) => {
                  if (item && typeof item === "object") {
                    if (fieldDef.type === "reference") {
                      // Si no tiene _id o el _id está vacío o el objeto tiene name: 'CARGANDO...', no incluirlo
                      if (!item._id || item._id === '' || item._id === null || item.name === 'CARGANDO...' || item.name === 'Cargando...') {
                        return null;
                      }
                      return item._id;
                    }
                  }
                  return item;
                })
                .filter((item) => item !== null && !isEmpty(item)); // Eliminar items nulos y vacíos
            } else {
              const value = data[fieldDef.key];
              
              // Para campos de tipo file, siempre incluirlos (incluso si son null)
              if (fieldDef.type === "file") {
                data[fieldDef.key] = value;
              }
              // Para campos calculados (raw_*), siempre incluirlos si tienen valor
              else if (fieldDef.key && fieldDef.key.startsWith('raw_') && !isEmpty(value)) {
                data[fieldDef.key] = value;
              }
              // Para otros campos, solo incluir si tienen un valor no vacío
              else if (!isEmpty(value)) {
                data[fieldDef.key] = value;
              } else {
                // IMPORTANTE: No establecer como null campos calculados, solo eliminarlos si están vacíos
                if (fieldDef.key && fieldDef.key.startsWith('raw_')) {
                  // Para campos calculados vacíos, eliminarlos en lugar de establecer como null
                  delete data[fieldDef.key];
              } else {
                data[fieldDef.key] = null;
                }
              }
            }

            if (fieldDef.hide) {
              delete data[key];
            }
          } else {
            // Si el campo no está en la definición pero es un campo calculado (empieza con "raw_"),
            // preservarlo si tiene un valor no vacío
            if (key.startsWith('raw_') && !isEmpty(data[key])) {
              // Preservar el campo calculado
              // No hacer nada, el campo ya está en data
            }
            // IMPORTANTE: Preservar campos que están en grupos visuales pero deben guardarse en el nivel raíz
            // Estos son campos que NO están definidos como parte de un grupo en modelFields
            // o que están definidos como campos del nivel raíz
            else {
              const fieldDef = this.modelFields.find(f => f.key === key);
              // Si el campo NO está en modelFields o está definido como campo del nivel raíz (no grupo),
              // preservarlo si tiene valor (es un campo de grupo visual que debe guardarse en el nivel raíz)
              if ((!fieldDef || (fieldDef && fieldDef.type !== 'group')) && !isEmpty(data[key])) {
               
                // Preservar el campo - no eliminarlo
              } else if (isEmpty(data[key])) {
                // Para otros campos no definidos, eliminarlos solo si están vacíos
                // IMPORTANTE: NO eliminar arrays de grupos aunque estén vacíos, porque pueden tener items nuevos
                if (key === 'invoicesupplier' && Array.isArray(data[key])) {
                  return; // Saltar la eliminación
                }
                // Campos movidos de grupos visuales al raíz: enviar null para que el backend pueda limpiarlos
                if (movedToRootFromVisualGroups.has(key)) {
                  data[key] = null;
                } else {
                  delete data[key];
                }
              } else {
                // Si tiene valor y no es un campo calculado conocido, preservarlo
                // (esto permite campos adicionales que puedan agregarse dinámicamente)
              }
            }
          }
        });
        return data;
      };

      // IMPORTANTE: Los visualGroups son solo para visualización, NO para la estructura de datos al guardar
      // Si hay grupos visuales en dataToSave, mover los campos al nivel raíz y eliminar el grupo
      const visualGroupsStartTime = performance.now();
      if (
        this.selectedModelData?.visualGroups &&
        this.selectedModelData.visualGroups.length > 0
      ) {
        
        this.selectedModelData.visualGroups.forEach((group) => {
          const groupKey = `group_${group.name}`;
          const isInvoicesupplierGroup = groupKey === 'group_invoicesupplier';
          
          if (dataToSave[groupKey] && typeof dataToSave[groupKey] === 'object') {
          
            // Mover los campos del grupo visual al nivel raíz (siempre, incluyendo vacíos)
            // Así al borrar un valor (ej. city) se envía '' o null y el backend puede limpiar el campo
            Object.keys(dataToSave[groupKey]).forEach((fieldKey) => {
              movedToRootFromVisualGroups.add(fieldKey);
              const valorEnGrupo = dataToSave[groupKey][fieldKey];
              const isTotalInvoiceSupplier = fieldKey === 'total_invoicesupplier';
              dataToSave[fieldKey] = valorEnGrupo;
              if (isTotalInvoiceSupplier) {
                // hook para total_invoicesupplier si se necesita
              }
            });
            
            // Eliminar el grupo visual (no debe guardarse en la base de datos)
            delete dataToSave[groupKey];
          
          }
        });
        
        processTimestamps.visualGroups = performance.now();
      }

      // Campos del sistema que nunca deben moverse (definir antes de usarlo)
      const systemFields = new Set(['_id', 'createdAt', 'updatedAt', 'createdBy', 'updatedBy', '__v', 'idModel', 'parent', 'editHistory', 'textPlainVersion', 'nid']);
      
      // Función genérica para verificar si un campo debe permanecer en el nivel raíz
      // Un campo debe permanecer en el nivel raíz si:
      // 1. Está definido en modelFields como un campo del nivel raíz (no grupo)
      // 2. Está en un grupo visual pero NO está definido como parte de un grupo en modelFields
      const shouldStayAtRootLevel = (fieldKey) => {
        const fieldDef = this.modelFields.find(f => f.key === fieldKey);
        // Si está definido como campo del nivel raíz (no grupo), debe permanecer ahí
        if (fieldDef && fieldDef.type !== 'group') {
          return true;
        }
        // Si NO está definido en modelFields, pero está en un grupo visual,
        // debe permanecer en el nivel raíz (los grupos visuales son solo para visualización)
        if (!fieldDef) {
          return true;
        }
        return false;
      };
      
      // IMPORTANTE: Preservar campos que están en grupos visuales pero deben guardarse en el nivel raíz
      // Estos son campos que NO están definidos como parte de un grupo en modelFields
      // o que están definidos como campos del nivel raíz
      const preservacionStartTime = performance.now();
      const preservedRootLevelFields = {};
      Object.keys(dataToSave).forEach(key => {
        // Solo preservar campos que no están en systemFields y que deben permanecer en el nivel raíz
        if (!systemFields.has(key) && shouldStayAtRootLevel(key)) {
          const value = dataToSave[key];
          if (value !== undefined && value !== null && value !== '') {
            preservedRootLevelFields[key] = value;
         
          }
        }
      });
      
      processTimestamps.preservacion = performance.now();

      // Procesar los campos en el nivel raíz según modelFields (no visualGroups)
      const processFieldsStartTime = performance.now();
      processFields(dataToSave, this.modelFields);
      processTimestamps.processFields = performance.now();
      
      // IMPORTANTE: Restaurar campos preservados después de processFields si fueron eliminados
      Object.keys(preservedRootLevelFields).forEach(key => {
        const preservedValue = preservedRootLevelFields[key];
        if (dataToSave[key] === undefined || dataToSave[key] === null || dataToSave[key] === '') {
          dataToSave[key] = preservedValue;
         
        } else if (key === 'total_invoicesupplier') {
         
        }
      });
      
      // SOLUCIÓN GENÉRICA: Detectar campos calculados del DOM y moverlos a sus grupos según modelFields
      // Analizar el fieldId de cada campo calculado para determinar su estructura (igual que ruleEngineMixin)
      const calculatedFieldsStartTime = performance.now();
      const calculatedFieldsToMove = []; // Array de {fieldKey, value, groupKey, groupIndex}
      
      // Buscar todos los inputs con clase "calculated-field" en el DOM
      const calculatedInputs = document.querySelectorAll('input.calculated-field');
      
      // Log de todos los IDs encontrados para debug (solo invoicesupplier)
      const allFieldIds = Array.from(calculatedInputs).map(input => ({
        id: input.id,
        value: input.value,
        hasValue: input.value && input.value !== ''
      }));
      
      // Identificar campos que tienen valor pero no se procesaron
      const processedFieldIds = new Set();
      
      calculatedInputs.forEach(input => {
        const fieldId = input.id;
        const value = input.value;
        
        // IMPORTANTE: Ignorar inputs sin ID válido (evita el warning)
        if (!fieldId || fieldId.trim() === '') {
          return;
        }
        
        if (!value || value === '') return;
        
        // Analizar el fieldId igual que en ruleEngineMixin.js
        // El formato es: group_*-*-*-...-N-fieldKey (donde N es un número 1-based)
        // Ejemplos:
        // - group_proform-productandprice-1-raw_totalqty (índice 1, primer elemento)
        // - group_proform-productandprice-2-raw_totalqty (índice 2, segundo elemento)
        
        let groupKey = null;
        let groupIndex = null;
        let fieldKey = null;
        
        // Primero, verificar si comienza con "group"
        if (fieldId.startsWith('group_')) {
          // Remover el prefijo "group_" y dividir por guiones
          const withoutPrefix = fieldId.substring(6); // "group_" tiene 6 caracteres
          const idParts = withoutPrefix.split('-');
          
          // Buscar el último número que sea el índice (antes del fieldKey)
          // El fieldKey puede tener guiones (ej: raw_totalqty, raw_totalpricecont)
          // Necesitamos encontrar el último número antes del fieldKey
          // Estrategia: buscar desde el final hacia atrás, el primer número que encontremos
          // que NO sea el último elemento es el índice
          for (let i = idParts.length - 2; i >= 0; i--) {
            const num = parseInt(idParts[i]);
            // Verificar que sea un número válido (>= 1 porque los índices son 1-based)
            if (!isNaN(num) && num >= 1) {
              groupIndex = num;
              groupKey = idParts.slice(0, i).join('-'); // Todo antes del índice
              fieldKey = idParts.slice(i + 1).join('-'); // Todo después del índice
              break;
            }
          }
          
          // Si no encontramos índice, el campo está en el nivel del grupo (no dentro de un elemento del array)
          if (groupIndex === null && idParts.length >= 1) {
            // El último elemento es el fieldKey, todo lo demás es el groupKey
            groupKey = idParts.slice(0, -1).join('-');
            fieldKey = idParts[idParts.length - 1];
          }
        } else {
          // Campo en nivel raíz (no tiene prefijo "group_")
          fieldKey = fieldId;
        }
        
        // Buscar en modelFields para determinar si el grupo es un array
        let targetGroupKey = null;
        let isArrayGroup = false;
        
        if (groupKey) {
          // Buscar el campo del modelo que corresponde a este grupo
          // El groupKey puede ser "productandprice" o "proform-productandprice"
          const groupKeyParts = groupKey.split('-');
          const lastPart = groupKeyParts[groupKeyParts.length - 1];
          
          // Buscar en modelFields
          const modelField = this.modelFields.find(f => f.key === lastPart || f.key === groupKey);
          if (modelField && modelField.type === 'group') {
            targetGroupKey = modelField.key;
            isArrayGroup = modelField.isArray === true;
          }
        }
        
        if (fieldKey && fieldKey.trim() !== '') {
          calculatedFieldsToMove.push({
            fieldKey,
            value: isNaN(value) ? value : Number(value),
            groupKey: targetGroupKey,
            groupIndex,
            isArrayGroup,
            originalFieldId: fieldId // Para debug
          });
          
          processedFieldIds.add(fieldId);
          
        } else if (fieldId && fieldId.trim() !== '') {
          // Filtrar IDs mal formados que empiezan con " = " o que son solo espacios y un valor
          const trimmedId = fieldId.trim();
          if (!trimmedId.startsWith('=') && trimmedId !== '') {
            console.warn(`⚠️ [processFormData] Campo calculado NO procesado (sin fieldKey): ${fieldId} = ${value}`);
          }
        }
      });
      
      // Función genérica para verificar si un campo debe permanecer en el nivel raíz
      const shouldStayAtRootLevelForCalculated = (fieldKey) => {
        const fieldDef = this.modelFields.find(f => f.key === fieldKey);
        // Si está definido como campo del nivel raíz (no grupo), debe permanecer ahí
        if (fieldDef && fieldDef.type !== 'group') {
          return true;
        }
        // Si NO está definido en modelFields, debe permanecer en el nivel raíz
        if (!fieldDef) {
          return true;
        }
        return false;
      };
      
      // Mover campos calculados a sus grupos correspondientes según modelFields
      calculatedFieldsToMove.forEach(({fieldKey, value, groupKey, groupIndex, isArrayGroup, originalFieldId}) => {
        // IMPORTANTE: NO mover campos que deben permanecer en el nivel raíz
        if (shouldStayAtRootLevelForCalculated(fieldKey)) {
          
          return; // Saltar este campo
        }
        
        if (groupKey && isArrayGroup) {
          // Campo pertenece a un grupo array
          if (dataToSave[groupKey] && Array.isArray(dataToSave[groupKey]) && dataToSave[groupKey].length > 0) {
            // Si hay groupIndex, mover al elemento específico (groupIndex es 1-based, convertir a 0-based)
            // Si no hay groupIndex, el campo está en el nivel del grupo (no dentro de un elemento)
            if (groupIndex !== null && groupIndex !== undefined) {
              const targetIndex = groupIndex - 1; // Convertir de 1-based a 0-based
              if (targetIndex >= 0 && targetIndex < dataToSave[groupKey].length && dataToSave[groupKey][targetIndex]) {
                dataToSave[groupKey][targetIndex][fieldKey] = value;
                delete dataToSave[fieldKey];
              }
            } else {
              // No hay groupIndex, el campo está en el nivel del grupo (no dentro de un elemento del array)
              // Esto significa que el campo calculado pertenece al grupo completo, no a un elemento específico
              // En este caso, debería estar en el nivel raíz del grupo, pero como es un array, no tiene sentido
              // Por ahora, moverlo al primer elemento como fallback
              if (dataToSave[groupKey] && dataToSave[groupKey][0]) {
                dataToSave[groupKey][0][fieldKey] = value;
                delete dataToSave[fieldKey];
              }
            }
          }
        } else if (groupKey && !isArrayGroup) {
          // Campo pertenece a un grupo simple (no array)
          if (dataToSave[groupKey] && typeof dataToSave[groupKey] === 'object' && !Array.isArray(dataToSave[groupKey])) {
            dataToSave[groupKey][fieldKey] = value;
            delete dataToSave[fieldKey];
          }
        }
        // Si no hay groupKey, el campo está en el nivel raíz y se queda ahí
      });
      
      processTimestamps.calculatedFields = performance.now();
      
      // Fallback genérico: Mover campos del nivel raíz que deberían estar en grupos array
      const fallbackStartTime = performance.now();
      // IMPORTANTE: Solo mover campos que NO fueron detectados en el DOM (no están en calculatedFieldsToMove)
      const movedFieldKeys = new Set(calculatedFieldsToMove.map(f => f.fieldKey));
      
      // Para cada grupo array en modelFields
      this.modelFields.forEach((modelField) => {
        if (modelField.type === 'group' && modelField.isArray === true) {
          const groupKey = modelField.key;
          
          // Verificar que el grupo existe y es un array con elementos
          if (dataToSave[groupKey] && Array.isArray(dataToSave[groupKey]) && dataToSave[groupKey].length > 0) {
            // Obtener todos los campos que están actualmente en el nivel raíz
            const rootLevelFields = Object.keys(dataToSave).filter(key => {
              // Excluir:
              // - Campos del sistema
              // - Campos que ya fueron movidos (detectados en DOM)
              // - El propio grupo array
              // - Campos vacíos/null/undefined
              // - Otros grupos array (no mover grupos array a otros grupos)
              // - Campos que están definidos en modelFields como campos del nivel raíz (no grupos)
              const isArrayGroup = Array.isArray(dataToSave[key]);
              const isOtherGroupArray = isArrayGroup && this.modelFields.some(f => f.key === key && f.type === 'group' && f.isArray === true);
              
              // Verificar si el campo está definido en modelFields como un campo del nivel raíz (no grupo)
              const fieldDef = this.modelFields.find(f => f.key === key);
              const isRootLevelField = fieldDef && fieldDef.type !== 'group';
              
              // IMPORTANTE: NO mover campos que están definidos en modelFields como campos del nivel raíz
              // Estos campos deben permanecer en el nivel raíz, no dentro de grupos array
              if (isRootLevelField) {
                return false; // No mover este campo
              }
              
              return !systemFields.has(key) &&
                     !movedFieldKeys.has(key) &&
                     key !== groupKey &&
                     !isOtherGroupArray && // IMPORTANTE: No mover otros grupos array
                     dataToSave[key] !== undefined &&
                     dataToSave[key] !== null &&
                     dataToSave[key] !== '';
            });
            
            // Para cada campo en el nivel raíz, verificar si debería estar en este grupo
            rootLevelFields.forEach(fieldKey => {
              // IMPORTANTE: NO mover campos que deben permanecer en el nivel raíz
              // (campos definidos como nivel raíz en modelFields o campos en grupos visuales)
              if (shouldStayAtRootLevel(fieldKey)) {
              
                return; // Saltar este campo
              }
              
              // Verificar si el campo NO está ya en el grupo (en ningún elemento del array)
              const fieldExistsInGroup = dataToSave[groupKey].some(item => 
                item && typeof item === 'object' && item[fieldKey] !== undefined
              );
              
              // Si el campo no existe en el grupo, moverlo al primer elemento como fallback
              // Esto es genérico y funciona para cualquier campo, no solo los definidos en modelField.fields
              if (!fieldExistsInGroup) {
                // Asegurar que el primer elemento existe y es un objeto
                if (dataToSave[groupKey][0] && typeof dataToSave[groupKey][0] === 'object') {
                  dataToSave[groupKey][0][fieldKey] = dataToSave[fieldKey];
                  delete dataToSave[fieldKey];
                }
              }
            });
          }
        }
      });

      // IMPORTANTE: Verificar y restaurar campos preservados antes de retornar
      // Esto asegura que los campos estén presentes incluso si fueron eliminados en algún paso intermedio
      Object.keys(preservedRootLevelFields).forEach(key => {
        const preservedValue = preservedRootLevelFields[key];
        if (dataToSave[key] === undefined || dataToSave[key] === null || dataToSave[key] === '') {
          dataToSave[key] = preservedValue;
         
        } else if (key === 'total_invoicesupplier') {
         
        }
      });

      processTimestamps.fallback = performance.now();
      processTimestamps.fin = performance.now();

      // Las fechas ahora se manejan como texto simple, no necesitan offset de zona horaria
      return dataToSave;
    },

    /**
     * A dónde volver tras guardar/cancelar/back en modo edición (no-add).
     * Si se llegó desde una vista concreta (routeViewId), volver a esa misma vista
     * (DynamicModelContentView) en lugar del detalle genérico del modelo.
     */
    navigateAfterEditDone(modelName, itemId) {
      if (this.routeViewId) {
        this.$router.push({
          name: "DynamicModelContentView",
          params: { modelName, itemId, viewId: this.routeViewId },
        });
      } else {
        this.$router.push(`/dynamic-model/${modelName}/view/${itemId}`);
      }
    },

    goBack() {
      if (this.embedded) {
        if (!this.isAddMode) this.releaseLock();
        return;
      }
      if (!this.isAddMode) this.releaseLock();
      const modelName = this.selectedModel || this.routeModelName;
      const itemId = this.routeItemId;
      if (this.isAddMode) {
        this.$router.push({ name: "DynamicModel", params: { modelName } });
      } else if (modelName && itemId) {
        this.navigateAfterEditDone(modelName, itemId);
      } else {
        this.$router.push({ name: "DynamicModelDetail", params: { modelName, itemId } });
      }
    },

    onCancel() {
      if (this.embedded) {
        if (!this.isAddMode) this.releaseLock();
        return;
      }
      if (!this.isAddMode) this.releaseLock();
      if (this.isAddMode) {
        this.$router.push({ name: "DynamicModel", params: { modelName: this.selectedModel } });
      } else if (this.routeViewId) {
        this.navigateAfterEditDone(this.selectedModel, this.routeItemId);
      } else {
        this.$router.push({
          name: "DynamicModelDetail",
          params: {
            modelName: this.selectedModel,
            itemId: this.routeItemId,
          },
        });
      }
    },

    // Métodos para manejar eventos del AutoSaveForm
    onUnsavedChanges() {
    },

    onAutoSaveSuccess() {
      this.savedCount += 1;
      const id = this.form && this.form._id;
      if (id) this.scheduleApplyPendingNotaComercialOffer(id);
    },

    onAutoSaveError(error) {
      console.error("❌ [DYNAMIC-MODEL-EDIT] Error en auto-guardado:", error);
    },

    /** Espera el alta/actualización automática en vuelo antes de guardar a mano. */
    async waitForPendingAutoSave() {
      const autoSaveForm = this.$refs.autoSaveForm;
      if (autoSaveForm && typeof autoSaveForm.waitForPendingSave === "function") {
        await autoSaveForm.waitForPendingSave();
      }
    },

  },
  async mounted() {
    await this.fetchModels();
    const autonomous = await this.probeAutonomousShell();
    if (autonomous) return;
    await this.fetchLists();
    if (!this.isAddMode && this.routeItemId) {
      await this.acquireLock();
    }
  },
  beforeDestroy() {
    if (this.lockInterval) {
      clearInterval(this.lockInterval);
    }
    if (!this.isAddMode && this.routeItemId) {
      this.releaseLock();
    }
  },
};
</script>

<style scoped>
.card {
  margin-top: 1rem;
}
.dynamic-model-edit--embedded {
  max-width: 100%;
  padding-left: 0;
  padding-right: 0;
}
.dynamic-model-edit--embedded .container-form {
  margin-top: 0;
}

/* Título y número del registro en la misma línea y a un tamaño de pantalla de
   trabajo: el h2 suelto se comía tres renglones arriba del formulario. */
.dynamic-model-edit-title {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 0.5rem;
  min-width: 0;
  font-size: 1.35rem;
  line-height: 1.2;
}

.dynamic-model-edit-title-ref {
  font-size: 0.95rem;
  font-weight: 400;
  color: #6c757d;
}
</style>
